'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/hooks/auth/use-auth'
import type { CapturedCustomerInfo, ChannelType, ConversationStatus } from '@/lib/agents/messaging/types'
import { useTranslations } from "next-intl"


export interface ConversationListItem {
  id: string
  channel_type: ChannelType
  contact_name: string | null
  /** Platform handle (`@username`) — Instagram and Telegram; the exclusion-list key. */
  contact_handle: string | null
  external_user_id: string
  contact_avatar_url: string | null
  status: ConversationStatus
  ai_paused: boolean
  /** Conversation-level opt-out from the agent's «pause AI on human intervention» setting. */
  auto_pause_exempt: boolean
  /** Pause origin (`metadata.ai_pause.by`); null for active or legacy rows. */
  ai_pause_by: string | null
  last_message_at: string | null
  created_at: string
  last_message_preview: string | null
  last_message_direction: 'inbound' | 'outbound' | null
  last_message_type: string | null
  /** The newest message is the automated offline reply — nobody has answered yet. */
  last_message_offline: boolean
  unread_count: number
  channel_account_name: string | null
  customer_info: CapturedCustomerInfo | null
  /** For `web_demo` rows: the Shopify storefront domain picked by the visitor. */
  shopify_demo_domain: string | null
}

export interface ConversationFilters {
  status?: ConversationStatus
  channel_type?: ChannelType
  search?: string
}

const CONVERSATIONS_POLL_INTERVAL = 15_000

export function useConversations(agentId: string | null, filters: ConversationFilters = {}) {
  const tUi = useTranslations("Feedback.AgentsMessagingUseConversations")

  const [conversations, setConversations] = useState<ConversationListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loadMoreFailed, setLoadMoreFailed] = useState(false)
  const cursorRef = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const pollingRef = useRef(false)
  const { fetchWithAuth, isAuthenticated } = useAuth()

  const fetchConversations = useCallback(async (reset = true) => {
    if (!agentId || !isAuthenticated) return

    // Abort any in-flight request (prevents stale filter results)
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      if (!reset && cursorRef.current) {
        params.set('cursor', cursorRef.current)
      }
      if (filters.status) params.set('status', filters.status)
      if (filters.channel_type) params.set('channel_type', filters.channel_type)
      if (filters.search) params.set('search', filters.search)

      const qs = params.toString()
      const res = await fetchWithAuth(
        `/api/agents/messaging/${agentId}/conversations${qs ? `?${qs}` : ''}`,
        { signal: controller.signal },
      )
      if (!res.ok) throw new Error(tUi("failedToFetchConversations"))

      const data = await res.json()

      if (reset) {
        setConversations(data.conversations)
      } else {
        setConversations((prev) => [...prev, ...data.conversations])
      }

      cursorRef.current = data.next_cursor
      setHasMore(!!data.next_cursor)
      setTotal(data.total)
      if (!reset) setLoadMoreFailed(false)
    } catch (err) {
      if ((err as Error).name === 'AbortError') return
      console.error('[useConversations] Error:', err)
      // Without this the sentinel re-arms over a still-visible target and retries
      // for as long as the failure lasts.
      if (!reset) setLoadMoreFailed(true)
    } finally {
      if (controller === abortRef.current) {
        setIsLoading(false)
      }
    }
  }, [agentId, isAuthenticated, filters.status, filters.channel_type, filters.search, fetchWithAuth, tUi])

  // Initial fetch + refetch on filter change
  useEffect(() => {
    cursorRef.current = null
    fetchConversations(true)
  }, [fetchConversations])

  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      fetchConversations(false)
    }
  }, [hasMore, isLoading, fetchConversations])

  const refresh = useCallback(() => {
    cursorRef.current = null
    fetchConversations(true)
  }, [fetchConversations])

  // Silent poll: update previews/unread counts, add new conversations
  const pollConversations = useCallback(async () => {
    if (!agentId || !isAuthenticated) return
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
    if (pollingRef.current) return // skip if poll already in flight

    pollingRef.current = true
    try {
      const params = new URLSearchParams()
      if (filters.status) params.set('status', filters.status)
      if (filters.channel_type) params.set('channel_type', filters.channel_type)
      if (filters.search) params.set('search', filters.search)

      const qs = params.toString()
      const res = await fetchWithAuth(
        `/api/agents/messaging/${agentId}/conversations${qs ? `?${qs}` : ''}`,
      )
      if (!res.ok) return

      const data = await res.json()
      const freshItems = data.conversations as ConversationListItem[]

      setConversations((prev) => {
        const existingIds = new Set(prev.map((c) => c.id))
        const freshMap = new Map(freshItems.map((c) => [c.id, c]))

        // Update existing items with fresh data (previews, unread counts)
        const updated = prev.map((c) => freshMap.get(c.id) || c)

        // Add truly new conversations
        const brandNew = freshItems.filter((c) => !existingIds.has(c.id))
        const merged = brandNew.length > 0 ? [...brandNew, ...updated] : updated

        // Re-sort by last_message_at descending so conversations with new messages float up
        return merged.sort((a, b) => {
          const ta = a.last_message_at ?? a.created_at
          const tb = b.last_message_at ?? b.created_at
          return tb.localeCompare(ta)
        })
      })
      setTotal(data.total)
    } catch {
      // Silent fail
    } finally {
      pollingRef.current = false
    }
  }, [agentId, isAuthenticated, fetchWithAuth, filters.status, filters.channel_type, filters.search])

  // Polling interval
  useEffect(() => {
    if (!agentId || !isAuthenticated) return

    const intervalId = setInterval(pollConversations, CONVERSATIONS_POLL_INTERVAL)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') pollConversations()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [agentId, isAuthenticated, pollConversations])

  /** Locally clear unread_count for a conversation (instant UI update, no server call) */
  const markAsRead = useCallback((conversationId: string) => {
    setConversations((prev) =>
      prev.map((c) => c.id === conversationId ? { ...c, unread_count: 0 } : c)
    )
  }, [])

  /** Locally patch a conversation's auto-pause flags (instant UI update, no server call) */
  const patchPauseState = useCallback((
    conversationId: string,
    patch: { ai_paused?: boolean; auto_pause_exempt?: boolean },
  ) => {
    setConversations((prev) =>
      prev.map((c) => {
        if (c.id !== conversationId) return c
        const next = { ...c, ...patch }
        // The origin label is meaningless once the AI resumes.
        return next.ai_paused ? next : { ...next, ai_pause_by: null }
      })
    )
  }, [])

  return { conversations, isLoading, total, hasMore, loadMoreFailed, loadMore, refresh, markAsRead, patchPauseState }
}
